import React, { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Swiper, SwiperSlide, useSwiper } from "swiper/react";
import { Pagination } from "swiper/modules";
import { CarouselSlide, SlideType } from "./slide";
import { ContentCard } from "../content-card";
import classNames from "classnames";
import { PaginationOptions } from "swiper/types";

import "swiper/css";
import "swiper/css/pagination";
import styles from "./styles.modules.scss";
import { useSnowplowContext } from "../../analytics/snowplow";
import { saveContentfulItems } from "../../utils/save-contentful-items";
import { EchoVaultSlide } from "./slide/echo-vault";
import { PollSlideType } from "./slide/poll";
import { ProgressSlideType } from "./slide/progress";

type CarouselProps = {
    locale: {
        lang?: string;
    };
    Slide: typeof CarouselSlide;
    slides: SlideType[];
    onSlideChange?: (index: number) => void;
};

function iosMajorVersion() {
    if (/iP(hone|od|ad)/.test(navigator.platform)) {
        const v = navigator.appVersion.match(/OS (\d+)_(\d+)_?(\d+)?/);

        const majorVersion = v && v[1] ? parseInt(v[1], 10) : null;

        return majorVersion;
    }

    return null;
}

export const SwiperCarousel = ({
    locale,
    Slide,
    slides,
    onSlideChange,
}: CarouselProps) => {
    const { trackImpression } = useSnowplowContext();
    const [searchParams] = useSearchParams();

    const useiOS12WorkAround = (iosMajorVersion() ?? Infinity) <= 12;

    const [time, setTime] = React.useState(Date.now());
    const lastAnalyticsId = React.useRef<string>("");

    const hasTimerLabel = slides.some((slide) => {
        const hasTimer = slide.type === "eventEntry";
        return hasTimer && slide.timerLabel;
    });

    saveContentfulItems("slides", slides);

    useEffect(() => {
        if (hasTimerLabel) {
            const intervalId = setInterval(() => {
                const currentTime = Date.now();
                setTime(currentTime);
            }, 1000);

            const clear = () => {
                clearInterval(intervalId);
            };

            return clear;
        }
    }, []);

    const filteredSlides = slides.filter((slide) => {
        const hasTimer = slide.type === "eventEntry";

        if (!hasTimer || !slide.timerKind) {
            return true;
        }

        if (slide.timerKind === "HIDE_ENTRY") {
            const shouldShow = slide.timerTarget > time;

            return shouldShow;
        }

        return true;
    });

    const paginationProps: PaginationOptions = {
        bulletActiveClass: classNames("selected", styles.selected),
        bulletClass: classNames("page", styles.page),
        enabled: filteredSlides.length > 1,
    };

    const sendAnalytics = (slideIndex: number) => {
        const currentSlide = filteredSlides[slideIndex];
        if (currentSlide && currentSlide.id !== lastAnalyticsId.current) {
            trackImpression(currentSlide.id, currentSlide.title);
            lastAnalyticsId.current = currentSlide.id;
        }
    };

    return (
        <Swiper
            onSlideChange={(swiper) => {
                sendAnalytics(swiper.realIndex);
                onSlideChange ? onSlideChange(swiper.realIndex) : null;
            }}
            onAfterInit={() => {
                // onSlideChange captures all the other carousel slide views, except the first one which we track here
                sendAnalytics(0);
            }}
            onSwiper={(swiper) => {
                const entryId = searchParams.get("openCarouselId");

                const possibleSlideIndex = filteredSlides.findIndex(
                    (slide) => slide.id === entryId,
                );

                if (possibleSlideIndex > -1) {
                    swiper.slideTo(possibleSlideIndex);
                }
            }}
            loop={true}
            pagination={paginationProps}
            modules={[Pagination]}
            className={classNames(styles.carousel, "carousel", {
                single: filteredSlides.length === 1,
                multi: filteredSlides.length > 1,
            })}
            onPaginationRender={(_, el) => {
                // There's no option for the container class name, so we use this
                el.classList.add("paginator");
                el.classList.add(styles.paginator ?? "");
            }}
        >
            {filteredSlides.map((slide) => {
                if (
                    slide.targeting?.campaignId === "echo-vault" &&
                    slide.type === "pollEntry"
                ) {
                    return null; // Skip rendering this slide
                }

                if (
                    slide.targeting?.campaignId === "echo-vault" &&
                    slide.type === "progressTrackerEntry"
                ) {
                    const swiper = useSwiper();

                    const echoVaultPollSlides = filteredSlides.filter(
                        (slide) =>
                            slide.type === "pollEntry" &&
                            slide.targeting?.campaignId === "echo-vault",
                    ) as (PollSlideType | undefined)[];

                    const sortedEchoVaultPollSlides = echoVaultPollSlides.sort(
                        (a, b) => {
                            if (!a || !b) return 0;

                            return (
                                // @ts-expect-error TS2345 Lazy
                                new Date(b.metadata.publishedAt).getTime() -
                                // @ts-expect-error TS2345 Lazy
                                new Date(a.metadata.publishedAt).getTime()
                            );
                        },
                    );

                    const latestEchoVaultPollSlide =
                        sortedEchoVaultPollSlides[0];

                    const echoVaultTrackerSlide = filteredSlides.find(
                        (slide) =>
                            slide.type === "progressTrackerEntry" &&
                            slide.targeting?.campaignId === "echo-vault",
                    ) as ProgressSlideType | undefined;

                    if (!latestEchoVaultPollSlide || !echoVaultTrackerSlide) {
                        return null;
                    }

                    return (
                        <SwiperSlide key="echo-vault" className="slide">
                            <div
                                className="echo-vault"
                                onClick={(e) => {
                                    if (!useiOS12WorkAround) {
                                        return;
                                    }

                                    if (
                                        (e.target as HTMLInputElement)
                                            .nodeName !== "DIV"
                                    ) {
                                        return;
                                    }

                                    swiper.slideNext();
                                }}
                            >
                                <ContentCard id="echo-vault" lang={locale.lang}>
                                    <EchoVaultSlide
                                        pollSlide={latestEchoVaultPollSlide}
                                        trackerSlide={echoVaultTrackerSlide}
                                    />
                                </ContentCard>
                            </div>
                        </SwiperSlide>
                    );
                }

                return (
                    <SwiperSlide key={slide.id} className="slide">
                        {() => {
                            const swiper = useSwiper();

                            return (
                                <div
                                    className={`${
                                        slide.targeting?.campaignId
                                            ? slide.targeting.campaignId
                                            : ""
                                    }`}
                                    onClick={(e) => {
                                        if (!useiOS12WorkAround) {
                                            return;
                                        }

                                        if (
                                            (e.target as HTMLInputElement)
                                                .nodeName !== "DIV"
                                        ) {
                                            return;
                                        }

                                        swiper.slideNext();
                                    }}
                                >
                                    <ContentCard
                                        id={slide.id}
                                        lang={locale.lang}
                                    >
                                        <Slide {...slide} />
                                    </ContentCard>
                                </div>
                            );
                        }}
                    </SwiperSlide>
                );
            })}
        </Swiper>
    );
};
